import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { usePageContent, deepMergeContent } from '@/hooks/usePageContent';
import { ScrollTimeline } from '@/components/destinations/ScrollTimeline';
import destinationData from './destinationData';
import CostOfLivingCalculator from '@/components/CostOfLivingCalculator';
import SEO from '@/components/SEO';
import CountryFlag from '@/components/CountryFlag';
import { 
  GraduationCap, 
  Building2, 
  Users,
  Banknote,
  Briefcase,
  Clock,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Star,
  Calendar,
  Award,
  X,
  Plus,
  Scale,
  MapPin,
  DollarSign,
  TrendingUp,
  BookOpen,
  Save as SaveIcon,

  Loader2,
  Trophy,
  Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API_URL = window.location.origin;

// Dynamic Universities Section Component - Fetches from CMS
const DynamicUniversitiesSection = ({ country, countryName, isDark }) => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Map destination country codes to database country names
  const countryMapping = {
    'usa': ['United States', 'USA'],
    'uk': ['United Kingdom', 'UK'],
    'canada': ['Canada'],
    'australia': ['Australia'],
    'germany': ['Germany'],
    'new-zealand': ['New Zealand'],
    'dubai': ['UAE', 'United Arab Emirates', 'Dubai'],
    'france': ['France'],
    'singapore': ['Singapore'],
    'malaysia': ['Malaysia'],
    'ireland': ['Ireland']
  };

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const mappedCountries = countryMapping[country] || [country];
        // Try each mapped country name
        let allUnis = [];
        let combinedTotal = 0;
        for (const c of mappedCountries) {
          const res = await fetch(`${API_URL}/api/university-pages/public/list?country=${encodeURIComponent(c)}&limit=6`);
          if (res.ok) {
            const data = await res.json();
            if (data.universities && data.universities.length > 0) {
              allUnis = [...allUnis, ...data.universities];
              combinedTotal += (data.total || 0);
            }
          }
        }
        // Remove duplicates by slug
        const uniqueUnis = allUnis.filter((uni, index, self) => 
          index === self.findIndex(u => u.slug === uni.slug)
        );
        setUniversities(uniqueUnis.slice(0, 6));
        // Set total ONCE (previously used a state-updater that accumulated
        // across React StrictMode double-invocations and inflated the count)
        setTotal(combinedTotal);
      } catch (error) {
        console.error('Error fetching universities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUniversities();
  }, [country]);

  if (loading) {
    return (
      <section className={`py-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-ux-cyan" />
          <p className={`mt-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Loading universities...</p>
        </div>
      </section>
    );
  }

  if (universities.length === 0) {
    return null; // Don't show section if no universities found
  }

  return (
    <section className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} id="universities" data-testid="dynamic-universities-section">
      {/* scroll target used by the "Find Universities in <country>" CTA above */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className={`mb-4 ${isDark ? 'bg-ux-green/20 text-ux-green' : 'bg-ux-green/10 text-ux-green'}`}>
            Popular Universities
          </Badge>
          <h2 className={`font-display text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            Explore <span className="gradient-text-electric">{countryName}</span> Universities
          </h2>
          <p className={`max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
            Get personalized guidance for admission to top universities with dedicated support throughout your journey.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {universities.map((uni) => (
            <Link 
              key={uni.slug} 
              to={`/universities/${uni.slug}`}
              className="group"
              data-testid={`dynamic-uni-card-${uni.slug}`}
            >
              <Card className={`h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
                isDark ? 'bg-[#233842] border-white/10 hover:border-ux-cyan/50' : 'hover:border-ux-cyan/50'
              }`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {uni.university_name}
                      </h3>
                      <p className={`text-sm flex items-center gap-1 mt-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        <MapPin className="h-3 w-3" />
                        {uni.city ? `${uni.city}, ` : ''}{uni.country}
                      </p>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {uni.world_ranking && (
                      <div className={`rounded-xl p-3 ${isDark ? 'bg-white/5' : 'bg-ux-slate-light'}`}>
                        <div className="flex items-center gap-1">
                          <Trophy className="h-3 w-3 text-amber-500" />
                          <p className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Ranking</p>
                        </div>
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>#{uni.world_ranking}</p>
                      </div>
                    )}
                    {uni.international_students && (
                      <div className={`rounded-xl p-3 ${isDark ? 'bg-white/5' : 'bg-ux-slate-light'}`}>
                        <div className="flex items-center gap-1">
                          <Globe className="h-3 w-3 text-ux-cyan" />
                          <p className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Int'l</p>
                        </div>
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{uni.international_students}</p>
                      </div>
                    )}
                  </div>

                  {uni.tagline && (
                    <p className={`text-sm line-clamp-2 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                      {uni.tagline}
                    </p>
                  )}

                  {/* Top QS Subject Ranking Chip */}
                  {uni.top_subject_ranking && (uni.top_subject_ranking.rank || uni.top_subject_ranking.rank_range) && (
                    <div className="mt-3" data-testid={`subject-rank-chip-${uni.slug}`}>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        isDark
                          ? 'bg-ux-cyan/15 text-ux-cyan border border-ux-cyan/30'
                          : 'bg-ux-blue/10 text-ux-blue border border-ux-blue/20'
                      }`}>
                        <Trophy className="h-3 w-3" />
                        {uni.top_subject_ranking.rank ? `#${uni.top_subject_ranking.rank}` : uni.top_subject_ranking.rank_range}
                        <span className="opacity-80">QS {uni.top_subject_ranking.subject}</span>
                      </span>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <span className={`inline-flex items-center text-sm font-medium ${isDark ? 'text-ux-cyan' : 'text-ux-blue'} group-hover:gap-2 transition-all`}>
                      View Details
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {total > 6 && (
          <div className="text-center mt-10">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-ux-green to-ux-cyan hover:opacity-90 text-white rounded-full px-8"
              asChild
            >
              <Link to={`/universities?country=${country}`}>
                View All {total} {countryName} Universities
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

// Country Comparison Section Component - CMS-backed for SEO
const CountryComparisonSection = ({ currentCountry, currentCountryName, isDark }) => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/destination-compare`);
        if (response.ok) {
          const data = await response.json();
          setDestinations(data);
        }
      } catch (error) {
        console.error('Error fetching destination comparisons:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, []);

  // Fallback data if CMS is empty
  const fallbackData = {
    usa: { flag: '🇺🇸', country_name: 'USA', tuition: '$20K - $60K', living_cost: '$15K - $25K', work_visa: '1-3 yrs (OPT)', pr_pathway: 'H-1B → Green Card', pr_difficulty: 'Hard', avg_salary: '$80K - $120K', top_for: ['Tech', 'Research', 'MBA'], highlight: 'Highest salaries' },
    canada: { flag: '🇨🇦', country_name: 'Canada', tuition: 'CAD 20K - 50K', living_cost: 'CAD 12K - 18K', work_visa: 'Up to 3 yrs (PGWP)', pr_pathway: 'Express Entry', pr_difficulty: 'Moderate', avg_salary: 'CAD 60K - 100K', top_for: ['PR Pathway', 'Tech', 'Business'], highlight: 'Best PR pathway' },
    uk: { flag: '🇬🇧', country_name: 'UK', tuition: '£15K - £35K', living_cost: '£12K - £18K', work_visa: '2 yrs (Graduate)', pr_pathway: '5 yrs Skilled Worker', pr_difficulty: 'Hard', avg_salary: '£35K - £65K', top_for: ['1-Year Masters', 'Finance', 'Law'], highlight: 'Fastest degree' },
    australia: { flag: '🇦🇺', country_name: 'Australia', tuition: 'AUD 25K - 45K', living_cost: 'AUD 21K - 28K', work_visa: '2-3 yrs (485)', pr_pathway: 'Skilled Migration', pr_difficulty: 'Moderate', avg_salary: 'AUD 70K - 100K', top_for: ['Lifestyle', 'Healthcare', 'Engineering'], highlight: 'Best weather & lifestyle' },
    germany: { flag: '🇩🇪', country_name: 'Germany', tuition: '€0 - €20K', living_cost: '€10K - €15K', work_visa: '18 months', pr_pathway: 'EU Blue Card', pr_difficulty: 'Moderate', avg_salary: '€45K - €70K', top_for: ['Engineering', 'Low Cost', 'Auto Industry'], highlight: 'Lowest tuition fees' },
    ireland: { flag: '🇮🇪', country_name: 'Ireland', tuition: '€10K - €25K', living_cost: '€10K - €15K', work_visa: '2 yrs (Stay Back)', pr_pathway: 'Stamp 4', pr_difficulty: 'Moderate', avg_salary: '€40K - €70K', top_for: ['Tech Hub', 'Pharma', 'Finance'], highlight: 'Tech company HQs' }
  };

  // Convert CMS data to map format, or use fallback
  const comparisonData = destinations.length > 0 
    ? destinations.reduce((acc, d) => {
        acc[d.country_code] = {
          flag: d.flag,
          country_name: d.country_name,
          tuition: d.tuition,
          living_cost: d.living_cost,
          work_visa: d.work_visa,
          pr_pathway: d.pr_pathway,
          pr_difficulty: d.pr_difficulty,
          avg_salary: d.avg_salary,
          top_for: d.top_for || [],
          highlight: d.highlight
        };
        return acc;
      }, {})
    : fallbackData;

  // Get comparison countries (exclude current)
  const otherCountries = Object.keys(comparisonData)
    .filter(key => key !== currentCountry)
    .slice(0, 4);

  const currentData = comparisonData[currentCountry];
  
  if (!currentData) return null;

  const comparisonFactors = [
    { key: 'tuition', label: 'Avg. Tuition/Year', icon: GraduationCap },
    { key: 'living_cost', label: 'Living Cost/Year', icon: Banknote },
    { key: 'work_visa', label: 'Post-Study Work', icon: Briefcase },
    { key: 'pr_pathway', label: 'PR Pathway', icon: Globe },
    { key: 'avg_salary', label: 'Avg. Starting Salary', icon: TrendingUp },
  ];

  if (loading) {
    return (
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-ux-cyan" />
          <p className={`mt-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Loading comparison data...</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="country-comparison-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className={`mb-4 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
            Compare Destinations
          </Badge>
          <h2 className={`font-display text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            How Does <span className="gradient-text-aurora">{currentCountryName}</span> Compare?
          </h2>
          <p className={`max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
            See how {currentCountryName} stacks up against other popular study destinations for Indian students
          </p>
        </div>

        {/* Desktop Comparison Table */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header Row */}
            <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: `180px repeat(${otherCountries.length + 1}, 1fr)` }}>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <span className={`text-sm font-medium ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Factor</span>
              </div>
              {/* Current Country - Highlighted */}
              <div className={`p-4 rounded-xl bg-gradient-to-br from-ux-cyan/20 to-ux-violet/20 border-2 border-ux-cyan`}>
                <div className="flex items-center gap-2 justify-center">
                  <CountryFlag country={currentData.name} emoji={currentData.flag} size="lg" />
                  <div className="text-center">
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{currentData.country_name}</p>
                    <Badge className="bg-ux-cyan/20 text-ux-cyan text-xs mt-1">Current</Badge>
                  </div>
                </div>
              </div>
              {/* Other Countries */}
              {otherCountries.map(key => (
                <Link 
                  key={key} 
                  to={`/destinations/${key}`}
                  className={`p-4 rounded-xl ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} transition-colors cursor-pointer`}
                >
                  <div className="flex items-center gap-2 justify-center">
                    <CountryFlag country={comparisonData[key].name} emoji={comparisonData[key].flag} size="lg" />
                    <div className="text-center">
                      <p className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{comparisonData[key].country_name}</p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>{comparisonData[key].highlight}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Data Rows */}
            {comparisonFactors.map((factor, index) => (
              <div 
                key={factor.key}
                className={`grid gap-3 mb-2`}
                style={{ gridTemplateColumns: `180px repeat(${otherCountries.length + 1}, 1fr)` }}
              >
                <div className={`p-4 rounded-xl flex items-center gap-2 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <factor.icon className={`h-4 w-4 ${isDark ? 'text-ux-cyan' : 'text-ux-violet'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{factor.label}</span>
                </div>
                {/* Current Country Value */}
                <div className={`p-4 rounded-xl text-center font-semibold bg-gradient-to-br from-ux-cyan/10 to-ux-violet/10 border border-ux-cyan/30 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {currentData[factor.key]}
                </div>
                {/* Other Countries Values */}
                {otherCountries.map(key => (
                  <div key={key} className={`p-4 rounded-xl text-center ${isDark ? 'text-white/80 bg-white/5' : 'text-ux-charcoal/80 bg-gray-50'}`}>
                    {comparisonData[key][factor.key]}
                  </div>
                ))}
              </div>
            ))}

            {/* Best For Row */}
            <div 
              className="grid gap-3 mt-4"
              style={{ gridTemplateColumns: `180px repeat(${otherCountries.length + 1}, 1fr)` }}
            >
              <div className={`p-4 rounded-xl flex items-center gap-2 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <Trophy className={`h-4 w-4 ${isDark ? 'text-ux-amber' : 'text-ux-amber'}`} />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Best For</span>
              </div>
              <div className={`p-4 rounded-xl bg-gradient-to-br from-ux-cyan/10 to-ux-violet/10 border border-ux-cyan/30`}>
                <div className="flex flex-wrap gap-1 justify-center">
                  {currentData.top_for.map(item => (
                    <Badge key={item} className="bg-ux-cyan/20 text-ux-cyan text-xs">{item}</Badge>
                  ))}
                </div>
              </div>
              {otherCountries.map(key => (
                <div key={key} className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {comparisonData[key].top_for.map(item => (
                      <Badge key={item} variant="outline" className={`text-xs ${isDark ? 'border-white/20 text-white/70' : ''}`}>{item}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Comparison Cards */}
        <div className="lg:hidden space-y-4">
          {/* Current Country Card */}
          <div className={`p-6 rounded-2xl bg-gradient-to-br from-ux-cyan/20 to-ux-violet/20 border-2 border-ux-cyan`}>
            <div className="flex items-center gap-3 mb-4">
              <CountryFlag country={currentData.name} emoji={currentData.flag} size="xl" />
              <div>
                <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{currentData.country_name}</h3>
                <Badge className="bg-ux-cyan text-white">You're viewing</Badge>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {comparisonFactors.slice(0, 4).map(factor => (
                <div key={factor.key} className={`p-3 rounded-xl ${isDark ? 'bg-white/10' : 'bg-white'}`}>
                  <p className={`text-xs mb-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>{factor.label}</p>
                  <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{currentData[factor.key]}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className={`text-xs mb-2 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Best For</p>
              <div className="flex flex-wrap gap-1">
                {currentData.top_for.map(item => (
                  <Badge key={item} className="bg-ux-cyan/20 text-ux-cyan text-xs">{item}</Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Other Countries */}
          <div className="grid grid-cols-2 gap-3">
            {otherCountries.map(key => (
              <Link 
                key={key}
                to={`/destinations/${key}`}
                className={`p-4 rounded-xl ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <CountryFlag country={comparisonData[key].name} emoji={comparisonData[key].flag} size="lg" />
                  <div>
                    <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{comparisonData[key].country_name}</p>
                    <p className={`text-xs ${isDark ? 'text-ux-cyan' : 'text-ux-violet'}`}>{comparisonData[key].highlight}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Work Visa</p>
                    <p className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>{comparisonData[key].work_visa}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>PR Difficulty</p>
                    <p className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>{comparisonData[key].pr_difficulty}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className={`mb-4 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
            Need help deciding? Our experts can guide you to the best destination for your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              className="bg-gradient-to-r from-ux-violet to-ux-cyan hover:opacity-90 text-white rounded-full px-6"
              asChild
            >
              <Link to="/compare">
                <Scale className="mr-2 h-4 w-4" />
                View Detailed Comparisons
              </Link>
            </Button>
            <Button 
              variant="outline"
              className={`rounded-full px-6 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-ux-violet text-ux-violet hover:bg-ux-violet hover:text-white'}`}
              asChild
            >
              <Link to="/contact">
                Talk to Expert
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};


// Comparison Modal Component
const ComparisonModal = ({ isOpen, onClose, universities, countryName }) => {
  const { isDark } = useTheme();
  if (!isOpen || universities.length === 0) return null;

  const comparisonFields = [
    { key: 'rank', label: 'World Ranking', icon: TrendingUp },
    { key: 'location', label: 'Location', icon: MapPin },
    { key: 'tuition', label: 'Tuition/Year', icon: DollarSign },
    // Acceptance Rate row removed — no verified source available
    { key: 'avgSalary', label: 'Avg. Salary (Post-Grad)', icon: Banknote },
    { key: 'studentCount', label: 'Total Students', icon: Users },
    { key: 'intlStudents', label: 'Int\'l Students', icon: GraduationCap },
    { key: 'duration', label: 'Program Duration', icon: Clock },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-5xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] text-white border-white/10' : ''}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 text-2xl ${isDark ? 'text-white' : ''}`}>
            <Scale className="h-6 w-6 text-ux-cyan" />
            Compare Universities
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {/* University Headers */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${universities.length}, 1fr)` }}>
            <div className={`font-semibold py-3 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Metric</div>
            {universities.map((uni) => (
              <div key={uni.id} className={`text-center p-4 rounded-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gradient-to-br from-ux-cyan/10 to-ux-violet/10'}`}>
                <Badge className="bg-ux-violet text-white mb-2">Rank #{uni.rank}</Badge>
                <h3 className={`font-bold text-sm md:text-base leading-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`} title={uni.name}>
                  {uni.name}
                </h3>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>{uni.location}</p>
              </div>
            ))}
          </div>

          {/* Comparison Rows */}
          <div className="mt-4 space-y-2">
            {comparisonFields.map((field, index) => (
              <div 
                key={field.key} 
                className={`grid gap-4 py-3 px-2 rounded-lg ${
                  index % 2 === 0
                    ? (isDark ? 'bg-white/[0.03]' : 'bg-gray-50')
                    : ''
                }`}
                style={{ gridTemplateColumns: `200px repeat(${universities.length}, 1fr)` }}
              >
                <div className={`flex items-center gap-2 font-medium ${isDark ? 'text-white/80' : 'text-ux-charcoal'}`}>
                  <field.icon className="h-4 w-4 text-ux-cyan" />
                  {field.label}
                </div>
                {universities.map((uni) => {
                  const value = uni[field.key];
                  const isRank = field.key === 'rank';
                  const isAcceptanceRate = field.key === 'acceptanceRate';
                  
                  // Highlight best values
                  let highlight = false;
                  if (isRank) {
                    const minRank = Math.min(...universities.map(u => u.rank));
                    highlight = uni.rank === minRank;
                  } else if (isAcceptanceRate) {
                    const maxRate = Math.max(...universities.map(u => parseInt(u.acceptanceRate)));
                    highlight = parseInt(uni.acceptanceRate) === maxRate;
                  }

                  return (
                    <div 
                      key={uni.id} 
                      className={`text-center font-semibold ${
                        highlight
                          ? 'text-ux-cyan bg-ux-cyan/10 rounded-lg py-1'
                          : (isDark ? 'text-white' : 'text-ux-charcoal')
                      }`}
                    >
                      {isRank ? `#${value}` : value}
                      {highlight && field.key === 'rank' && (
                        <Star className="h-3 w-3 inline ml-1 text-ux-cyan fill-ux-cyan" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Programs Row */}
            <div 
              className={`grid gap-4 py-3 px-2 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}
              style={{ gridTemplateColumns: `200px repeat(${universities.length}, 1fr)` }}
            >
              <div className={`flex items-center gap-2 font-medium ${isDark ? 'text-white/80' : 'text-ux-charcoal'}`}>
                <BookOpen className="h-4 w-4 text-ux-blue" />
                Top Programs
              </div>
              {universities.map((uni) => (
                <div key={uni.id} className="text-center">
                  <div className="flex flex-wrap gap-1 justify-center">
                    {uni.programs.map((prog) => (
                      <Badge key={prog} variant="outline" className={`text-xs ${isDark ? 'border-white/20 text-white/80' : ''}`}>
                        {prog}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons — "Save Comparison" removed (Student Portal is Phase 2).
              "Talk to Expert" removed since it duplicated "Talk to a Counsellor". */}
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <Button
              className="bg-gradient-to-r from-ux-violet to-ux-cyan hover:opacity-90 text-white"
              asChild
              data-testid="compare-talk-counsellor-btn"
            >
              <Link to="/contact?source=comparison_modal">
                Talk to a Counsellor
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Floating Comparison Bar Component
const ComparisonBar = ({ selectedUniversities, onRemove, onCompare, maxSelections = 3 }) => {
  if (selectedUniversities.length === 0) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-ux-charcoal via-ux-violet to-ux-charcoal text-white p-4 shadow-2xl z-50 animate-fade-in"
      data-testid="comparison-bar"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-ux-cyan" />
          <span className="font-medium">Compare Universities</span>
          <Badge className="bg-ux-cyan text-white">{selectedUniversities.length}/{maxSelections}</Badge>
        </div>
        
        <div className="flex items-center gap-3 flex-1 justify-center">
          {selectedUniversities.map((uni) => (
            <div 
              key={uni.id}
              className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5"
            >
              <span className="text-sm font-medium">{uni.shortName}</span>
              <button 
                onClick={() => onRemove(uni.id)}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${uni.shortName}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          
          {selectedUniversities.length < maxSelections && (
            <div className="text-white/50 text-sm">
              Select {maxSelections - selectedUniversities.length} more to compare
            </div>
          )}
        </div>

        <Button 
          onClick={onCompare}
          disabled={selectedUniversities.length < 2}
          className="bg-ux-coral text-white hover:bg-ux-coral-dark disabled:opacity-50 shadow-glow-coral"
          data-testid="compare-now-btn"
        >
          Compare Now
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const DestinationPage = () => {
  const { country } = useParams();
  const { isDark } = useTheme();
  const { getDestinationHero } = useAssetContext();
  // CMS override per country (slug: destination-<country>)
  const { content: cmsOverride } = usePageContent(`destination-${country || 'usa'}`);
  const fallbackData = destinationData[country] || destinationData.usa;
  const data = deepMergeContent(fallbackData, cmsOverride);
  
  // Get hero images from CMS/AssetContext (fallback to data if not available)
  const heroImageDesktop = getDestinationHero(country || 'usa', false) || data.heroImage;
  const heroImageMobile = getDestinationHero(country || 'usa', true) || data.heroImageMobile;

  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [realSlugMap, setRealSlugMap] = useState({}); // normalized-name -> real CMS slug
  const [statsOverride, setStatsOverride] = useState(null); // CMS-managed stats bar
  const maxSelections = 3;

  // Load CMS-managed stats bar override for this destination
  useEffect(() => {
    const slugForApi = (country || '').replace(/-/g, '_');
    if (!slugForApi) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/assets/destination-stats/public/${slugForApi}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.stats) && data.stats.length > 0) {
          setStatsOverride(data.stats);
        } else if (!cancelled) {
          setStatsOverride(null);
        }
      } catch (e) {
        // silent - fallback to default stats
      }
    })();
    return () => { cancelled = true; };
  }, [country]);

  // Fetch real university slugs for this country from CMS so "View Full Details" links point to actual pages
  useEffect(() => {
    const normalize = (s) => (s || '').toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]+/g, ' ').trim();
    const countryMapping = {
      'usa': ['United States', 'USA'],
      'uk': ['United Kingdom', 'UK'],
      'canada': ['Canada'],
      'australia': ['Australia'],
      'germany': ['Germany'],
      'new-zealand': ['New Zealand'],
      'dubai': ['UAE', 'United Arab Emirates', 'Dubai'],
      'france': ['France'],
      'singapore': ['Singapore'],
      'malaysia': ['Malaysia'],
      'ireland': ['Ireland']
    };
    const fetchSlugs = async () => {
      try {
        const mapped = countryMapping[country] || [country];
        const map = {};
        for (const c of mapped) {
          const res = await fetch(`${API_URL}/api/university-pages/public/list?country=${encodeURIComponent(c)}&limit=100`);
          if (res.ok) {
            const json = await res.json();
            (json.universities || []).forEach((u) => {
              if (u.university_name && u.slug) map[normalize(u.university_name)] = u.slug;
            });
          }
        }
        setRealSlugMap(map);
      } catch (e) {
        console.error('Failed to load slug map', e);
      }
    };
    fetchSlugs();
  }, [country]);

  // Resolve a real CMS slug for a static university name; null if none exists
  const resolveUniversitySlug = (name) => {
    const normalize = (s) => (s || '').toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]+/g, ' ').trim();
    return realSlugMap[normalize(name)] || null;
  };

  const toggleUniversitySelection = (uni) => {
    setSelectedUniversities(prev => {
      const isSelected = prev.some(u => u.id === uni.id);
      if (isSelected) {
        return prev.filter(u => u.id !== uni.id);
      } else if (prev.length < maxSelections) {
        return [...prev, uni];
      }
      return prev;
    });
  };

  const removeFromComparison = (uniId) => {
    setSelectedUniversities(prev => prev.filter(u => u.id !== uniId));
  };

  const openCompareModal = () => {
    if (selectedUniversities.length >= 2) {
      setIsCompareModalOpen(true);
    }
  };

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'} transition-colors duration-500`} data-testid={`destination-page-${country}`}>
      {/* SEO for Destination Pages */}
      <SEO 
        title={`Study in ${data.name} | Universities, Visa, Admission Guide`}
        description={`Study in ${data.name} from India. ${data.stats.universities} universities, ${data.stats.internationalStudents} international students. Get complete guide on admission, visa, costs & scholarships. Expert counseling from UniXperts.`}
        keywords={`study in ${data.name.toLowerCase()}, ${data.name.toLowerCase()} student visa, ${data.name.toLowerCase()} universities for Indian students, ${data.name.toLowerCase()} admission, ${data.name.toLowerCase()} education cost, ${data.name.toLowerCase()} scholarships, best universities in ${data.name.toLowerCase()}, ${data.name.toLowerCase()} PR pathway`}
        canonical={`https://unixperts.com/destinations/${country}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `Complete Guide to Study in ${data.name}`,
          "description": data.tagline,
          "image": heroImageDesktop,
          "author": {
            "@type": "Organization",
            "name": "UniXperts"
          },
          "publisher": {
            "@type": "Organization",
            "name": "UniXperts",
            "logo": {
              "@type": "ImageObject",
              "url": "https://unixperts.com/logo512.png"
            }
          },
          "datePublished": "2024-01-01",
          "dateModified": new Date().toISOString().split('T')[0]
        }}
      />
      
      {/* Hero Section with Responsive Images */}
      <section className="relative h-[60vh] min-h-[400px] flex items-end overflow-hidden">
        {/* Mobile Hero Image */}
        {heroImageMobile && (
          <div 
            className="absolute inset-0 bg-cover bg-center md:hidden"
            style={{ backgroundImage: `url(${heroImageMobile})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </div>
        )}
        {/* Desktop Hero Image */}
        <div 
          className={`absolute inset-0 bg-cover bg-center ${heroImageMobile ? 'hidden md:block' : ''}`}
          style={{ backgroundImage: `url(${heroImageDesktop})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          <Badge className="bg-white/20 text-white backdrop-blur-sm mb-4">
            Study Destination
          </Badge>
          <div className="flex items-center gap-4 mb-4">
            <CountryFlag country={data.name} emoji={data.flag} size="xl" />
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
              Study in {data.name}
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl">
            {data.tagline}
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-gradient-to-r from-ux-violet via-ux-cyan to-ux-blue py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {(() => {
            const defaultStats = [
              { value: data.stats.universities, label: 'Universities' },
              { value: data.stats.internationalStudents, label: "Int'l Students" },
              { value: data.stats.workVisa, label: 'Work Visa' },
              { value: data.stats.intakes, label: 'Intake Seasons' },
            ];
            const rows = statsOverride && statsOverride.length ? statsOverride : defaultStats;
            const cols = Math.min(rows.length, 4);
            const gridClass = cols >= 4
              ? 'grid grid-cols-2 md:grid-cols-4 gap-4 text-white text-center'
              : `grid grid-cols-1 md:grid-cols-${cols} gap-4 text-white text-center`;
            return (
              <div className={gridClass} data-testid="destination-stats-bar">
                {rows.slice(0, 4).map((s, idx) => (
                  <div key={idx}>
                    <p className="font-display text-2xl font-bold">{s.value}</p>
                    <p className="text-sm text-white/80">{s.label}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Overview */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className={`mb-4 ${isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-cyan/10 text-ux-cyan'}`}>Overview</Badge>
              <h2 className={`font-display text-3xl md:text-4xl font-bold mb-6 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Why Study in <span className="gradient-text-electric">{data.name}</span>?
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                {data.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full px-8"
                  onClick={() => {
                    const el = document.getElementById('universities');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  data-testid="cta-find-universities"
                >
                  Find Universities in {data.name}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className={`rounded-full px-8 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-ux-violet text-ux-violet hover:bg-ux-violet hover:text-white'}`}
                  asChild
                  data-testid="cta-book-consultation"
                >
                  <Link to={`/contact?destination=${encodeURIComponent(data.name)}&source=destination_${country}`}>
                    Book Consultation
                  </Link>
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {data.whyStudy.slice(0, 4).map((item, index) => (
                <div key={index} className="glass-panel rounded-3xl p-5 hover:scale-[1.02] transition-all">
                  <CheckCircle2 className="h-6 w-6 text-ux-cyan mb-3" />
                  <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{item.title}</h3>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/70'}`}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Top Universities with Compare Feature */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <Badge className={`mb-4 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>Top Universities</Badge>
              <h2 className={`font-display text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Leading Universities in <span className="gradient-text-aurora">{data.name}</span>
              </h2>
              <p className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>
                Select up to 3 universities to compare side-by-side
              </p>
            </div>
            {selectedUniversities.length >= 2 && (
              <Button 
                onClick={openCompareModal}
                className="bg-gradient-to-r from-ux-cyan to-ux-violet text-white hover:opacity-90 shadow-glow-cyan"
                data-testid="compare-header-btn"
              >
                <Scale className="mr-2 h-4 w-4" />
                Compare ({selectedUniversities.length})
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.topUniversities.map((uni) => {
              const isSelected = selectedUniversities.some(u => u.id === uni.id);
              const isMaxReached = selectedUniversities.length >= maxSelections && !isSelected;

              return (
                <div 
                  key={uni.id} 
                  className={`glass-panel rounded-3xl p-6 cursor-pointer hover:scale-[1.02] transition-all ${
                    isSelected ? 'ring-2 ring-ux-cyan' : ''
                  } ${isMaxReached ? 'opacity-60' : ''}`}
                  onClick={() => !isMaxReached && toggleUniversitySelection(uni)}
                  data-testid={`university-card-${uni.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>Rank #{uni.rank}</Badge>
                        {isSelected && (
                          <Badge className="bg-ux-cyan text-white">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Selected
                          </Badge>
                        )}
                      </div>
                      <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{uni.name}</h3>
                      <p className={`text-sm flex items-center gap-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        <MapPin className="h-3 w-3" />
                        {uni.location}
                      </p>
                    </div>
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected 
                        ? 'bg-ux-cyan border-ux-cyan text-white' 
                        : isDark ? 'border-white/30 hover:border-ux-cyan' : 'border-gray-300 hover:border-ux-cyan'
                    }`}>
                      {isSelected ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <Plus className={`h-4 w-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                    <div className={`rounded-xl p-3 ${isDark ? 'bg-white/5' : 'bg-ux-slate-light'}`}>
                      <p className={isDark ? 'text-white/60' : 'text-ux-charcoal/60'}>Tuition</p>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{uni.tuition}</p>
                    </div>
                    <div className={`rounded-xl p-3 ${isDark ? 'bg-white/5' : 'bg-ux-slate-light'}`}>
                      <p className={isDark ? 'text-white/60' : 'text-ux-charcoal/60'}>Duration</p>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{uni.duration || '—'}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {uni.programs.map((prog) => (
                      <Badge key={prog} variant="outline" className={`text-xs ${isDark ? 'border-white/20 text-white/70' : ''}`}>
                        {prog}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* View Details Link */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    {(() => {
                      const realSlug = resolveUniversitySlug(uni.name);
                      const target = realSlug ? `/universities/${realSlug}` : `/universities?country=${country}`;
                      return (
                        <Link 
                          to={target}
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center text-sm font-medium ${isDark ? 'text-ux-cyan hover:text-ux-cyan/80' : 'text-ux-blue hover:text-ux-blue/80'}`}
                        >
                          {realSlug ? 'View Full Details' : 'Explore Universities'}
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-ux-violet to-ux-blue hover:opacity-90 text-white rounded-full px-8"
              asChild
            >
              <Link to={`/universities?country=${country}`}>
                Explore All {data.name} Universities
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Costs & Requirements */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="costs" className="w-full">
            <TabsList className={`grid w-full max-w-md mx-auto grid-cols-3 mb-12 ${isDark ? 'bg-white/5' : ''}`}>
              <TabsTrigger value="costs">Costs</TabsTrigger>
              <TabsTrigger value="requirements">Requirements</TabsTrigger>
              <TabsTrigger value="scholarships">Scholarships</TabsTrigger>
            </TabsList>
            
            <TabsContent value="costs">
              <div className="max-w-3xl mx-auto">
                <div className="glass-panel rounded-3xl p-8">
                  <h3 className={`font-display text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    <Banknote className="h-6 w-6 text-ux-blue" />
                    Cost of Studying in {data.name}
                  </h3>
                  <div className="space-y-4">
                    <div className={`flex justify-between items-center py-3 border-b ${isDark ? 'border-white/10' : ''}`}>
                      <span className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>Tuition Fees (Annual)</span>
                      <span className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{data.costs.tuitionRange}</span>
                    </div>
                    <div className={`flex justify-between items-center py-3 border-b ${isDark ? 'border-white/10' : ''}`}>
                      <span className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>Living Expenses (Annual)</span>
                      <span className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{data.costs.livingCost}</span>
                    </div>
                    <div className={`flex justify-between items-center py-3 rounded-xl px-4 ${isDark ? 'bg-ux-cyan/10' : 'bg-ux-blue-light'}`}>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Total Annual Cost</span>
                      <span className="font-bold text-ux-cyan text-lg">{data.costs.totalAnnual}</span>
                    </div>
                  </div>
                  <p className={`text-sm mt-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    * Costs vary based on university, program, and city. Contact us for personalized estimates.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="requirements">
              <div className="max-w-3xl mx-auto">
                <div className="glass-panel rounded-3xl p-8">
                  <h3 className={`font-display text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Eligibility Requirements
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <GraduationCap className="h-5 w-5 text-ux-cyan" />
                        Academic Requirements
                      </h4>
                      <ul className="space-y-2">
                        {data.requirements.academic.map((req, i) => (
                          <li key={i} className={`flex items-start gap-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                            <CheckCircle2 className="h-4 w-4 text-ux-green mt-1 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <Award className="h-5 w-5 text-ux-cyan" />
                        Test Scores
                      </h4>
                      <ul className="space-y-2">
                        {data.requirements.tests.map((test, i) => (
                          <li key={i} className={`flex items-start gap-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                            <CheckCircle2 className="h-4 w-4 text-ux-green mt-1 flex-shrink-0" />
                            {test}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <Building2 className="h-5 w-5 text-ux-cyan" />
                        Documents Required
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {data.requirements.documents.map((doc, i) => (
                          <Badge key={i} variant="secondary" className={isDark ? 'bg-white/10 text-white' : ''}>{doc}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="scholarships">
              <div className="max-w-3xl mx-auto">
                <div className="glass-panel rounded-3xl p-8">
                  <h3 className={`font-display text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    <Star className="h-6 w-6 text-ux-amber" />
                    Scholarships in {data.name}
                  </h3>
                  <div className="space-y-4">
                    {data.scholarships.map((scholarship, i) => (
                      <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-ux-slate-light'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{scholarship.name}</h4>
                          <Badge className="bg-ux-green/20 text-ux-green">{scholarship.coverage}</Badge>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                          Eligibility: {scholarship.eligibility}
                        </p>
                      </div>
                    ))}
                  </div>
                  <Button 
                    className="w-full mt-6 bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full"
                    asChild
                  >
                    <Link to="/contact">
                      Ask About Scholarships
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Cost of Living Calculator */}
      <CostOfLivingCalculator country={country} countryName={data.name} />

      {/* Country Comparison Section */}
      <CountryComparisonSection currentCountry={country} currentCountryName={data.name} isDark={isDark} />

      {/* Application Timeline - Interactive Scroll */}
      <ScrollTimeline timeline={data.timeline} countryName={data.name} intakes={data.intakes} />

      {/* Dynamic Universities from CMS */}
      <DynamicUniversitiesSection country={country} countryName={data.name} isDark={isDark} />

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-blue via-ux-violet to-ux-cyan" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-6 text-white">
            Ready to Study in {data.name}?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Our expert counselors are ready to help you find the perfect university and guide you through every step of your journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-ux-violet hover:bg-ux-green hover:text-ux-charcoal font-bold rounded-full px-8 py-6 text-lg hover:scale-105 transition-all"
              onClick={() => {
                const el = document.getElementById('universities');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              data-testid="cta-find-universities-bottom"
            >
              Find Universities in {data.name}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="hero-outline"
              className="rounded-full px-8 py-6 text-lg"
              asChild
              data-testid="cta-book-consultation-bottom"
            >
              <Link to={`/contact?destination=${encodeURIComponent(data.name)}&source=destination_${country}_bottom`}>Book Free Consultation</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Floating Comparison Bar */}
      <ComparisonBar 
        selectedUniversities={selectedUniversities}
        onRemove={removeFromComparison}
        onCompare={openCompareModal}
        maxSelections={maxSelections}
      />

      {/* Comparison Modal */}
      <ComparisonModal 
        isOpen={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        universities={selectedUniversities}
        countryName={data.name}
      />
    </div>
  );
};
